export class PathUtils {
    constructor() {}

    /**
     * Joins arguments to a path
     * joinPath("http://google.com/", "my", "path") will return "http://google.com/my/path"
     * joinPath("//a", "", "/", "/b/") will return "//a/b"
     * joinPath() will return ""
     *
     * @param args
     */
    static joinPath(...args: any[]): string {
        return args.map((part, i) => {
            if (i === 0) {
                return part.trim().replace(/[\/]*$/g, "");
            } else {
                return part.trim().replace(/(^[\/]*|[\/]*$)/g, "");
            }
        }).filter((x) => x.length).join("/");
    }
    static getLayerPath(layer: __esri.Layer
        | __esri.MapImageLayer | __esri.FeatureLayer | __esri.Sublayer | __esri.SubtypeGroupLayer
        | __esri.SubtypeSublayer | __esri.TileLayer | nullish): string | null {
        // parsedUrl on Layer is not documented!
        if (!layer) {
            return null;
        }
        let url = (layer as any).parsedUrl?.path;
        if (!url && "url" in layer) {
            url = (layer as __esri.MapImageLayer).url; // MapImageLayer Service
        }
        return url;
    }
    static getMetaId( path: string | null): string {
        if(typeof path !== "string"){
            return "";
        }
        path = path.replace(/\/(FeatureServer|MapServer|ImageServer|VectorTileServer)(|\/|\/(\d+))$/i, "");
        path = path.replace(/\/VectorTileServer\/(.+)\.json$/i, "");
        path = path.substring(path.lastIndexOf("/") + 1);
        path = path.replace(/_map$/i, "");
        path = path.replace(/_v(\d+)(_FT|_MP|_IM|_VT|$)/i, ""); // Version und Feature/Map Services Postfix entfernen
        return path;
    }
    static getLayerId( path: string): number | null {
        let layerId = null;
        const matches = path.match(/\/(FeatureServer|MapServer|ImageServer)(|\/|\/(\d+))$/i);
        path = path.replace(/\/VectorTileServer\/(.+)\.json$/i, "");
        if(matches && matches.length === 4 && typeof matches[3] !== "undefined"){
            layerId = parseInt(matches[3], 10);
        }
        return layerId;
    }
    static getVersion( path: string | null): number | null {
        let version = null;
        if(typeof path === "string") {
            const matches = path.match(/_v(\d+)(_FT|_MP|_IM|_VT|$)/i);
            if (matches && matches.length === 3 && typeof matches[1] !== "undefined") {
                version = parseInt(matches[1], 10);
            }
        }
        return version;
    }
    static readableUrl(url: string): string {
        const addr = new URL(url);
        url = addr.hostname;
        url = url.replace(/^www\./i, "");
        if (addr.pathname !== "/" || addr.search !=="" || addr.hash !==""){
            url += addr.pathname + addr.search + addr.hash;
        }
        return url;
    }
    // https://stackoverflow.com/questions/1053902/how-to-convert-a-title-to-a-url-slug-in-jquery
    static slug(str: string): string {
        str = str.replace(/^\s+|\s+$/g, ""); // trim
        str = str.toLowerCase();

        // remove accents, swap ñ for n, etc
        const from = "ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆĞÍÌÎÏİŇÑÓÖÒÔÕØŘŔŠŞŤÚŮÜÙÛÝŸŽ"
            + "áäâàãåčçćďéěëèêẽĕȇğíìîïıňñóöòôõøðřŕšşťúůüùûýÿžþÞĐđßÆa·/_,:;";
        const to   = "AAAAAACCCDEEEEEEEEGIIIIINNOOOOOORRSSTUUUUUYYZ"
            + "aaaaaacccdeeeeeeeegiiiiinnooooooorrsstuuuuuyyzbBDdBAa------";
        for (let i=0, l=from.length ; i<l ; i++) {
            str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
        }
        str = str.replace(/[^a-z0-9 -]/g, "") // remove invalid chars
            .replace(/\s+/g, "-") // collapse whitespace and replace by -
            .replace(/-+/g, "-"); // collapse dashes

        return str;
    };
    /**
     * JS Implementation of MurmurHash3 (r136) (as of May 20, 2011)
     *
     * @author <a href="mailto:gary.court@gmail.com">Gary Court</a>
     * @see http://github.com/garycourt/murmurhash-js
     * @author <a href="mailto:aappleby@gmail.com">Austin Appleby</a>
     * @see http://sites.google.com/site/murmurhash/
     *
     * @param {string} key ASCII only
     * @param {number} seed Positive integer only, default 42
     * @return {number} 32-bit positive integer hash
     */

    static murmurhash3(key: string, seed?: number) {
        let h1b; let k1; let i;

        const remainder: number = key.length & 3; // key.length % 4
        const bytes: number = key.length - remainder;
        let h1: number = typeof seed === "undefined"? 42: seed;
        const c1: number = 0xcc9e2d51;
        const c2: number = 0x1b873593;
        i = 0;

        while (i < bytes) {
            k1 =
                ((key.charCodeAt(i) & 0xff)) |
                ((key.charCodeAt(++i) & 0xff) << 8) |
                ((key.charCodeAt(++i) & 0xff) << 16) |
                ((key.charCodeAt(++i) & 0xff) << 24);
            ++i;

            k1 = ((((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16))) & 0xffffffff;
            k1 = (k1 << 15) | (k1 >>> 17);
            k1 = ((((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16))) & 0xffffffff;

            h1 ^= k1;
            h1 = (h1 << 13) | (h1 >>> 19);
            h1b = ((((h1 & 0xffff) * 5) + ((((h1 >>> 16) * 5) & 0xffff) << 16))) & 0xffffffff;
            h1 = (((h1b & 0xffff) + 0x6b64) + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16));
        }

        k1 = 0;

        switch (remainder) {
        case 3: k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16;
        case 2: k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8;
        case 1: k1 ^= (key.charCodeAt(i) & 0xff);

            k1 = (((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
            k1 = (k1 << 15) | (k1 >>> 17);
            k1 = (((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
            h1 ^= k1;
        }

        h1 ^= key.length;

        h1 ^= h1 >>> 16;
        h1 = (((h1 & 0xffff) * 0x85ebca6b) + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
        h1 ^= h1 >>> 13;
        h1 = ((((h1 & 0xffff) * 0xc2b2ae35) + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16))) & 0xffffffff;
        h1 ^= h1 >>> 16;

        return h1 >>> 0;
    }
}

